/* BIBAB premium product gallery. Scoped to the main product section only. */
(() => {
  'use strict';

  const mobile = window.matchMedia('(max-width: 749px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const locale = (document.documentElement.lang || 'nl').slice(0, 2).toLowerCase();
  const words = {
    nl: { previous: 'Vorige foto', next: 'Volgende foto', photo: 'Foto', of: 'van' },
    en: { previous: 'Previous photo', next: 'Next photo', photo: 'Photo', of: 'of' },
    de: { previous: 'Vorheriges Foto', next: 'Naechstes Foto', photo: 'Foto', of: 'von' }
  }[locale] || { previous: 'Previous photo', next: 'Next photo', photo: 'Photo', of: 'of' };

  const arrow = (direction) => {
    const path = direction === 'left' ? 'M15 18l-6-6 6-6' : 'M9 18l6-6-6-6';
    return '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="' + path + '"/></svg>'; 
  };

  function initProduct(product) {
    const gallery = product.querySelector('media-gallery');
    if (!gallery || gallery.dataset.bibabGalleryReady === 'true') return;
    const viewer = gallery.querySelector('[id^="GalleryViewer-"]');
    const slider = viewer && viewer.querySelector('[id^="Slider-Gallery-"]');
    if (!slider) return;

    let items = Array.from(slider.children).filter((item) => item.classList.contains('product__media-item'));
    if (!items.length) return;

    gallery.classList.add('bibab-gallery');
    gallery.dataset.bibabGalleryReady = 'true';

    // Shopify's "image_zoom: none" still renders a clickable modal toggle.
    // Remove image toggles rather than hiding a functional zoom control.
    const disableImageZoom = () => {
      gallery.querySelectorAll('.product__modal-opener--image .product__media-toggle').forEach((toggle) => {
        toggle.remove();
      });
    };
    disableImageZoom();

    const controls = document.createElement('div');
    controls.className = 'bibab-gallery__controls';
    controls.setAttribute('aria-label', 'Product gallery');

    const prev = document.createElement('button');
    prev.type = 'button';
    prev.className = 'bibab-gallery__arrow';
    prev.setAttribute('aria-label', words.previous);
    prev.innerHTML = arrow('left');

    const next = document.createElement('button');
    next.type = 'button';
    next.className = 'bibab-gallery__arrow';
    next.setAttribute('aria-label', words.next);
    next.innerHTML = arrow('right');

    controls.append(prev, next);
    viewer.appendChild(controls);

    const pagination = document.createElement('div');
    pagination.className = 'bibab-gallery__pagination';
    const counter = document.createElement('span');
    counter.className = 'bibab-gallery__counter';
    counter.setAttribute('aria-live', 'polite');
    const dots = document.createElement('div');
    dots.className = 'bibab-gallery__dots';
    dots.setAttribute('role', 'group');
    pagination.append(dots, counter);
    viewer.insertAdjacentElement('afterend', pagination);

    const allItems = () => Array.from(slider.children).filter((item) => item.classList.contains('product__media-item'));
    const dotsFor = () => Array.from(dots.querySelectorAll('button'));

    const selectedIndex = () => {
      if (!items.length) return 0;
      if (!mobile.matches) {
        const active = items.findIndex((item) => item.classList.contains('is-active'));
        return active < 0 ? 0 : active;
      }
      const firstLeft = items[0].offsetLeft;
      const position = slider.scrollLeft;
      let index = 0;
      let smallestDistance = Infinity;
      items.forEach((item, i) => {
        const distance = Math.abs(item.offsetLeft - firstLeft - position);
        if (distance < smallestDistance) {
          smallestDistance = distance;
          index = i;
        }
      });
      return index;
    };

    const updateControls = (index) => {
      controls.hidden = items.length < 2;
      pagination.hidden = items.length < 2;
      if (items.length < 2) return;
      prev.disabled = index <= 0;
      next.disabled = index >= items.length - 1;
      counter.textContent = (index + 1) + ' / ' + items.length;
      dotsFor().forEach((dot, i) => {
        if (i === index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    };

    const activate = (index, smooth = true) => {
      items = allItems();
      if (!items.length) return;
      index = Math.max(0, Math.min(index, items.length - 1));
      items.forEach((item, i) => item.classList.toggle('is-active', i === index));
      if (mobile.matches) {
        const left = items[index].offsetLeft - items[0].offsetLeft;
        slider.scrollTo({ left, behavior: smooth && !reducedMotion.matches ? 'smooth' : 'instant' });
      }
      updateControls(index);
    };

    const refreshDots = () => {
      items = allItems();
      dots.replaceChildren();
      items.forEach((item, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'bibab-gallery__dot';
        dot.setAttribute('aria-label', words.photo + ' ' + (index + 1) + ' ' + words.of + ' ' + items.length);
        dot.addEventListener('click', () => activate(index));
        dots.appendChild(dot);
      });
      updateControls(selectedIndex());
    };

    prev.addEventListener('click', () => activate(selectedIndex() - 1));
    next.addEventListener('click', () => activate(selectedIndex() + 1));

    // On mobile the native Dawn slider remains touch/swipe enabled.
    let scrollFrame = null;
    slider.addEventListener('scroll', () => {
      if (!mobile.matches || scrollFrame !== null) return;
      scrollFrame = window.requestAnimationFrame(() => {
        scrollFrame = null;
        const index = selectedIndex();
        items.forEach((item, i) => item.classList.toggle('is-active', i === index));
        updateControls(index);
      });
    }, { passive: true });

    // Live embroidery remains on image 1. Do NOT automatically change the
    // visible gallery slide while a customer is typing the dog's name:
    // this can pull the viewport away from the input field.
    // Keep the existing thread-colour preview behaviour unchanged.
    product.addEventListener('change', (event) => {
      if (event.target.matches('input[data-bibab-color]') && selectedIndex() !== 0) activate(0);
    });

    // React to slide insertions and slide selection only. Text and
    // ornament changes in the live embroidery must be ignored.
    const observer = new MutationObserver((changes) => {
      let slidesChanged = false;
      let selectionChanged = false;
      changes.forEach((change) => {
        if (change.type === 'childList' && change.target === slider) {
          const nodes = [...change.addedNodes, ...change.removedNodes];
          if (nodes.some((node) => node.nodeType === 1 && node.classList &&
              node.classList.contains('product__media-item'))) {
            slidesChanged = true;
          }
        }
        if (change.type === 'attributes' && change.attributeName === 'class' &&
            change.target.parentElement === slider && change.target.classList &&
            change.target.classList.contains('product__media-item')) {
          selectionChanged = true;
        }
      });
      if (slidesChanged) {
        disableImageZoom();
        refreshDots();
      } else if (selectionChanged) {
        updateControls(selectedIndex());
      }
    });
    observer.observe(slider, {
      childList: true,
      attributes: true,
      attributeFilter: ['class'],
      subtree: true
    });

    mobile.addEventListener('change', () => {
      const active = items.findIndex((item) => item.classList.contains('is-active'));
      window.requestAnimationFrame(() => activate(Math.max(0, active), false));
    });

    refreshDots();
    activate(0, false);
  }

  function init(scope) {
    if (scope.matches && scope.matches('product-info[id^="MainProduct-"]')) initProduct(scope);
    scope.querySelectorAll('product-info[id^="MainProduct-"]').forEach(initProduct);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document));
  else init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
